import React, { useEffect, useCallback } from 'react';
import * as Styled from './CategoriesPickerV2.styles';
import { useIsMobileDevice } from '../../../utils';
import HorizontalScroll from '../../Elements/HorizontalScroll/HorizontalScroll';
import { SimpleTab } from '../SimpleTab';
import { SimpleTabType, Sizes, Filter } from '../SimpleTab/SimpleTab';

type Props = {
  tabs: SimpleTabType[];
  subTabs?: SimpleTabType[];
  showAllLabel?: string;
  showAllNumber?: number | string;
  isShowAllActive?: boolean;
  isFilterActive?: boolean;
  appliedFiltersNumber?: number;
  shouldResetScroll?: boolean;
  shouldShowFiltersToggle?: boolean;
  shouldShowBackButton?: boolean;
  shouldShowAllButton?: boolean;
  onTabClick: (value?: Filter) => void;
  onSubTabsClick: (value?: Filter) => void;
  onSeeAllClick: () => void;
  onFilterToggle: () => void;
  onBackButtonClick: () => void;
  rightElement?: JSX.Element;
  rightElementWidth?: number;
  shouldShowSecondLine?: boolean;
  withMobilePadding?: boolean;
  withMobileScrollPadding?: boolean;
  filterValue?: string;
  backButtonText?: string;
};

const CategoriesPickerV2: React.FC<Props> = ({
  showAllLabel = 'Show All',
  backButtonText = 'Back',
  tabs = [],
  subTabs,
  onSubTabsClick,
  onTabClick,
  onSeeAllClick,
  onBackButtonClick,
  rightElement,
  showAllNumber,
  isShowAllActive,
  isFilterActive,
  onFilterToggle,
  shouldResetScroll = false,
  shouldShowFiltersToggle = true,
  shouldShowBackButton = false,
  appliedFiltersNumber = 0,
  rightElementWidth = 0,
  shouldShowSecondLine = true,
  withMobilePadding = false,
  withMobileScrollPadding = false,
  shouldShowAllButton = true,
  filterValue,
}) => {
  const isMobile = useIsMobileDevice('md');
  const horizontalRef = React.useRef<{ resetScroll: () => void }>(null);

  useEffect(() => {
    if (shouldResetScroll) {
      horizontalRef.current?.resetScroll();
    }
  }, [shouldResetScroll]);

  const handleCategoryClick = useCallback(
    (filter?: Filter) => {
      onTabClick?.(filter);
    },
    [onTabClick],
  );

  const handleSubcategoryClick = useCallback(
    (filter?: Filter) => {
      onSubTabsClick?.(filter);
    },
    [onSubTabsClick],
  );

  const handleBackButtonClick = useCallback(() => {
    onBackButtonClick?.();
  }, [onBackButtonClick]);

  const handleFilterToggle = useCallback(() => {
    onFilterToggle?.();
  }, [onFilterToggle]);

  const filterTabs = (tab: SimpleTabType) => {
    if (!filterValue || tab.selected) return true;
    return tab.title?.toLowerCase()?.includes(filterValue?.toLowerCase());
  };

  const hasRightElement = rightElement != null;

  return (
    <Styled.Wrapper withMobilePadding={withMobilePadding}>
      <HorizontalScroll
        withMobilePadding={withMobileScrollPadding}
        isDraggable={false}
        showArrows={!isMobile}
        arrowScrollingPercent={0.8}
        parentRef={horizontalRef}
      >
        <Styled.CategoriesWrapper>
          {shouldShowBackButton && (
            <SimpleTab
              onTabClick={handleBackButtonClick}
              title={backButtonText}
              leftIconName="arrow-left"
              counter={undefined}
            />
          )}
          {shouldShowAllButton && (
            <SimpleTab
              onTabClick={onSeeAllClick}
              title={showAllLabel}
              counter={showAllNumber}
              selected={isShowAllActive}
            />
          )}
          {tabs?.filter(filterTabs).map((tab, index) => (
            <SimpleTab
              key={String(index + (tab?.id || ''))}
              onTabClick={handleCategoryClick}
              {...tab}
            />
          ))}
          <Styled.MarginAddon withMobilePadding={withMobilePadding} />
        </Styled.CategoriesWrapper>
      </HorizontalScroll>
      {shouldShowSecondLine && (
        <Styled.SubcategoriesWrapper hasRightElement={hasRightElement}>
          <Styled.SubcategoriesScrollerWrapper widthLimit={rightElementWidth}>
            <HorizontalScroll
              isDraggable={false}
              showArrows={!isMobile}
              arrowScrollingPercent={0.8}
              wrapperClassName="subcategories"
            >
              <Styled.CategoriesWrapper>
                {shouldShowFiltersToggle && (
                  <SimpleTab
                    onTabClick={handleFilterToggle}
                    title="Filters"
                    leftIconName="filters"
                    counter={
                      appliedFiltersNumber > 0
                        ? appliedFiltersNumber
                        : undefined
                    }
                    selected={isFilterActive}
                    size={Sizes.small}
                  />
                )}
                {subTabs?.map((tab, index) => (
                  <SimpleTab
                    key={index}
                    onTabClick={handleSubcategoryClick}
                    {...tab}
                    size={Sizes.small}
                  />
                ))}
              </Styled.CategoriesWrapper>
            </HorizontalScroll>
          </Styled.SubcategoriesScrollerWrapper>
          {hasRightElement ? rightElement : null}
        </Styled.SubcategoriesWrapper>
      )}
    </Styled.Wrapper>
  );
};

export default CategoriesPickerV2;
